import { Component, ChangeDetectionStrategy, inject, computed, input } from '@angular/core';
import { Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { take } from 'rxjs';

import { ButtonModule } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { SplitButtonModule } from 'primeng/splitbutton';
import { TooltipModule } from 'primeng/tooltip';
import { DialogService } from 'primeng/dynamicdialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MenuItem } from 'primeng/api';

import { CreateOrder, QuoteClient, QuoteStatus } from 'src/api-service';
import { CurrencyWarningDialogComponent } from 'src/app/pages/quote/dialog/currency-warning-dialog/currency-warning-dialog.component';
import { MarkAsLostReasonDialogComponent } from './dialogs/mark-as-lost-reason-dialog/mark-as-lost-reason-dialog.component';
import { ReviewOrderDialogComponent } from './dialogs/review-order-dialog/review-order-dialog.component';
import { selectQuoteState, selectQuoteIsValidSalesSupportPerson, selectIsSubscriptionQuote } from 'src/app/store/pages/quote/quote.selectors';
import { selectIsCentralSalesSupport, selectIsDrawer, selectIsSuperUser, selectUserContext } from 'src/app/store/user/user.selectors';
import { selectCanSendForDrawing, selectSendForDrawingBlockers } from 'src/app/store/pages/quote/drawing/drawing.selectors';
import { SendForDrawingDialogComponent } from './dialogs/send-for-drawing-dialog/send-for-drawing-dialog.component';
import { StartDrawingDialogComponent } from './dialogs/start-drawing-dialog/start-drawing-dialog.component';
import { CompleteDrawingDialogComponent } from './dialogs/complete-drawing-dialog/complete-drawing-dialog.component';
import { RevokeDrawingDialogComponent } from './dialogs/revoke-drawing-dialog/revoke-drawing-dialog.component';
import { selectVersion } from 'src/app/store/pages/quote/version/version.selectors';
import { selectHasDraftItems } from 'src/app/store/pages/quote/articles/articles.selectors';
import {
    selectIsValidForOrder,
    selectValidationErrorCount,
    selectHasItemErrors,
    selectHasProjectErrors,
    selectSubscriptionLockKey,
} from 'src/app/store/pages/quote/validation/validation.selectors';
import { SendQuoteStepperComponent } from './dialogs/send-quote-stepper/send-quote-stepper.component';
import { CreateOrderStepperComponent } from './dialogs/create-order-stepper/create-order-stepper.component';
import { DuplicateQuoteComponent } from './dialogs/quote-duplicate/duplicate-quote.component';
import { DuplicateQuoteWarningDialogComponent } from './dialogs/duplicate-quote-warning-dialog/duplicate-quote-warning-dialog.component';
import { RevertQuoteDialogComponent } from './dialogs/revert-quote-dialog/revert-quote-dialog.component';
import { CreateVersionDialogComponent } from '../version-actions/dialogs/create-version-dialog/create-version-dialog.component';
import { LocaleService } from 'src/app/shared/util/locale.service';

export type QuoteActionsMode = 'primary' | 'menu';

@Component({
    selector: 'app-quote-actions',
    templateUrl: './quote-actions.component.html',
    styleUrl: './quote-actions.component.scss',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ButtonModule, MenuModule, SplitButtonModule, TooltipModule, TranslateModule],
})
export class QuoteActionsComponent {
    // =========================================================================
    // INPUTS
    // =========================================================================
    public readonly mode = input<QuoteActionsMode>('menu');

    // =========================================================================
    // DEPENDENCY INJECTION
    // =========================================================================
    private readonly store = inject(Store);
    private readonly router = inject(Router);
    private readonly dialogService = inject(DialogService);
    private readonly translate = inject(TranslateService);
    private readonly locale = inject(LocaleService);
    private readonly quoteClient = inject(QuoteClient);

    // =========================================================================
    // STORE SIGNALS
    // =========================================================================
    public readonly quote = toSignal(this.store.select(selectQuoteState));
    public readonly selectedVersion = toSignal(this.store.select(selectVersion));
    public readonly hasDraftItems = toSignal(this.store.select(selectHasDraftItems), { initialValue: false });
    public readonly isValidForOrder = toSignal(this.store.select(selectIsValidForOrder), { initialValue: true });
    public readonly validationErrorCount = toSignal(this.store.select(selectValidationErrorCount), { initialValue: 0 });
    public readonly hasItemErrors = toSignal(this.store.select(selectHasItemErrors), { initialValue: false });
    public readonly hasProjectErrors = toSignal(this.store.select(selectHasProjectErrors), { initialValue: false });
    public readonly subscriptionLockKey = toSignal(this.store.select(selectSubscriptionLockKey), { initialValue: null });
    private readonly isValidSalesSupportPerson = toSignal(this.store.select(selectQuoteIsValidSalesSupportPerson), { initialValue: false });
    private readonly user = toSignal(this.store.select(selectUserContext));
    public readonly isSubscription = toSignal(this.store.select(selectIsSubscriptionQuote), { initialValue: false });
    public readonly isDrawer = toSignal(this.store.select(selectIsDrawer), { initialValue: false });
    private readonly isCentralSalesSupport = toSignal(this.store.select(selectIsCentralSalesSupport), { initialValue: false });
    private readonly isSuperUser = toSignal(this.store.select(selectIsSuperUser), { initialValue: false });
    private readonly canSendForDrawing = toSignal(this.store.select(selectCanSendForDrawing), { initialValue: false });
    private readonly sendForDrawingBlockers = toSignal(this.store.select(selectSendForDrawingBlockers), { initialValue: [] as string[] });

    // =========================================================================
    // COMPUTED - Status helpers
    // =========================================================================
    public readonly isDraft = computed(() => this.selectedVersion()?.status === QuoteStatus.Draft);
    public readonly isSent = computed(() => this.selectedVersion()?.status === QuoteStatus.Sent);
    public readonly isUnderReview = computed(() => this.selectedVersion()?.status === QuoteStatus.UnderReview);
    public readonly isReadyForReview = computed(() => this.selectedVersion()?.status === QuoteStatus.ReadyForReview);
    public readonly isApproved = computed(() => this.selectedVersion()?.status === QuoteStatus.Approved);
    public readonly isLost = computed(() => this.quote()?.status === QuoteStatus.Lost);
    public readonly isAutoArchived = computed(() => this.quote()?.status === QuoteStatus.AutoArchived);
    public readonly isReadyForDrawing = computed(() => this.quote()?.status === QuoteStatus.ReadyForDrawing);
    public readonly isDrawingInProgress = computed(() => this.quote()?.status === QuoteStatus.DrawingInProgress);

    // **** DRAWING BUTTONS **** //
    public readonly showSendForDrawingBtn = computed(() =>
        this.isSubscription() && (this.isDraft() || this.isSent()),
    );
    public readonly hasCompletedDrawing = computed(() => !!this.quote()?.hasCompletedDrawing);
    public readonly sendForDrawingDisabled = computed(() => !this.canSendForDrawing() || this.hasCompletedDrawing());
    public readonly sendForDrawingTooltip = computed(() => {
        this.locale.currentLang();
        if (this.hasCompletedDrawing()) {
            return this.translate.instant("QUOTE.DRAWING.BLOCKER_ALREADY_DRAWN");
        }
        const blockers = this.sendForDrawingBlockers();
        if (blockers.length === 0) return null;
        return blockers.map(key => this.translate.instant(key)).join("\n");
    });

    public readonly showStartDrawingBtn = computed(() => this.isDrawer() && this.isReadyForDrawing());
    public readonly showCompleteDrawingBtn = computed(() => this.isDrawer() && this.isDrawingInProgress());

    /** Replaces the "Send for drawing" slot once the quote is actually on the detour.
     *  Visible to anyone — salesperson and drawer alike — so the request can be
     *  cancelled from either side. */
    public readonly showRevokeDrawingBtn = computed(() =>
        (this.isReadyForDrawing() || this.isDrawingInProgress())
        && (this.isCentralSalesSupport() || this.isSuperUser()),
    );

    public readonly isQuoteLostOrRemoved = computed(() => {
        const status = this.quote()?.status;
        return status === QuoteStatus.Lost || status === QuoteStatus.Removed;
    });

    // =========================================================================
    // COMPUTED - Show actions
    // =========================================================================
    public readonly showActions = computed(() => {
        return this.isDraft() || this.isSent() || this.isUnderReview() || this.isReadyForReview() || this.isApproved() || this.isLost() || this.isAutoArchived() || this.isReadyForDrawing() || this.isDrawingInProgress();
    });

    /** Primary button has no meaningful action while drawing — hide it. */
    public readonly showPrimaryBtn = computed(() => !this.isReadyForDrawing() && !this.isDrawingInProgress());

    // =========================================================================
    // COMPUTED - Primary button config
    // =========================================================================
    public readonly primaryLabel = computed(() => {
        this.locale.currentLang();
        if (this.isAutoArchived()) return this.translate.instant('VERSION.STATUS.AUTOARCHIVED');
        if (this.isLost()) return this.translate.instant('VERSION.STATUS.LOST');
        if (this.isApproved()) return this.translate.instant('VERSION.STATUS.APPROVED');
        if (this.isDraft()) return this.translate.instant('QUOTE.DETAIL.BTN_SEND_QUOTE');
        if (this.isSent() || this.isUnderReview()) return this.translate.instant('QUOTE.DETAIL.BTN_CREATE_ORDER');
        if (this.isReadyForReview()) return this.translate.instant('QUOTE.DETAIL.BTN_REVIEW');
        return '';
    });

    public readonly primaryIcon = computed(() => {
        if (this.isAutoArchived()) return 'pi pi-server';
        if (this.isLost()) return 'pi pi-times-circle';
        if (this.isApproved()) return 'pi pi-check-circle';
        if (this.isDraft()) return 'pi pi-send'; // Next: Sent
        if (this.isSent()) return 'pi pi-check-circle'; // Next: Approved
        if (this.isUnderReview()) return 'pi pi-check-circle'; // Next: Approved
        if (this.isReadyForReview()) return 'pi pi-eye'; // Next: UnderReview
        return '';
    });

    public readonly primarySeverity = computed(() => {
        if (this.isAutoArchived()) return 'secondary';
        if (this.isLost()) return 'danger';
        if (this.isApproved()) return 'success';
        if (this.isDraft()) return 'info'; // Next: Sent (blue)
        if (this.isSent()) return 'success'; // Next: Approved (green)
        if (this.isUnderReview()) return 'success'; // Next: Approved (green)
        if (this.isReadyForReview()) return 'pink'; // Next: UnderReview (pink)
        return 'success';
    });

    // =========================================================================
    // COMPUTED - Disabled state
    // =========================================================================
    public readonly isPrimaryDisabled = computed(() => {
        if (this.isQuoteLostOrRemoved()) return true;
        if (this.isAutoArchived()) return true;
        if (this.isApproved()) return true;
        if (this.hasDraftItems()) return true;
        if (this.hasItemErrors() && !this.isDraft()) return true;

        // Block sending quote if project management config is invalid
        // (does not apply to subscription quotes — they don't require project management).
        if (this.isDraft() && this.hasProjectErrors() && !this.isSubscription()) return true;

        // Block sending subscription quote if interest rate is missing or outdated
        if (this.isDraft() && this.subscriptionLockKey()) return true;

        // UnderReview - endast sales support kan agera
        if (this.isUnderReview() && !this.isValidSalesSupportPerson()) {
            return true;
        }

        // Extra validation for "Skapa order"
        if ((this.isSent() || this.isUnderReview()) && !this.isValidForOrder()) {
            return true;
        }

        // Subscription orders require financing-company contract — gated to super-users.
        if ((this.isSent() || this.isUnderReview()) && this.isSubscription() && !this.isSuperUser()) {
            return true;
        }

        return false;
    });

    public readonly disabledTooltip = computed(() => {
        this.locale.currentLang();
        if (this.hasDraftItems()) {
            return this.translate.instant('QUOTE.DETAIL.SAVE_DRAFTS_FIRST');
        }
        if (this.hasItemErrors() && !this.isDraft()) {
            return this.translate.instant('QUOTE.DETAIL.SAVE_DRAFTS_FIRST');
        }
        if (this.isDraft() && this.hasProjectErrors() && !this.isSubscription()) {
            return this.translate.instant('CREATE_ORDER.VALIDATION.ERR_PROJECT_MANAGEMENT_REQUIRED');
        }
        const subKey = this.subscriptionLockKey();
        if (this.isDraft() && subKey) {
            return this.translate.instant(subKey);
        }
        if (this.isUnderReview() && !this.isValidSalesSupportPerson()) {
            return this.translate.instant('QUOTE.DETAIL.QUOTE_LOCKED.NOT_SALES_SUPPORT_MESSAGE');
        }
        if ((this.isSent() || this.isUnderReview()) && !this.isValidForOrder()) {
            const count = this.validationErrorCount();
            return this.translate.instant('QUOTE.TOOLBAR.VALIDATION_ERRORS', { count });
        }
        if ((this.isSent() || this.isUnderReview()) && this.isSubscription() && !this.isSuperUser()) {
            return this.translate.instant('QUOTE.DETAIL.SUBSCRIPTION_ORDER_SUPER_USER_REQUIRED');
        }
        return null;
    });

    // =========================================================================
    // COMPUTED - Cog menu items (ambient actions only — non state-changing)
    // =========================================================================
    public readonly menuItems = computed((): MenuItem[] => {
        this.locale.currentLang();
        const items: MenuItem[] = [];
        items.push({
            label: this.translate.instant('DUPLICATE_QUOTE.TITLE'),
            icon: 'pi pi-copy',
            command: () => this.onDuplicateClicked(),
        });
        if (this.isSuperUser() && (this.isSent() || this.isUnderReview())) {
            items.push({
                label: this.translate.instant('QUOTE.DETAIL.BTN_PREVIEW_ORDER_JSON'),
                icon: 'pi pi-eye',
                command: () => this.onPreviewOrderClicked(),
            });
        }
        return items;
    });

    // =========================================================================
    // COMPUTED - Split-button dropdown (state-changing alternatives)
    //
    // The primary button still drives the most common path forward; this menu
    // surfaces the alternatives — sending for drawing, reverting from review,
    // marking as lost. Each item gates on the same conditions as before, just
    // in a more discoverable place than the cog.
    // =========================================================================
    public readonly splitMenuItems = computed((): MenuItem[] => {
        this.locale.currentLang();
        const items: MenuItem[] = [];
        const quoteStatus = this.quote()?.status;
        const versionStatus = this.selectedVersion()?.status;
        const terminal = quoteStatus === QuoteStatus.Lost
                      || quoteStatus === QuoteStatus.Removed
                      || quoteStatus === QuoteStatus.AutoArchived;

        // Skicka för ritning — alternative path from Draft/Sent/Ready/UnderReview
        // on subscription quotes. Disabled when already drawn or validation fails.
        if (this.isSubscription() && !terminal && !this.isReadyForDrawing() && !this.isDrawingInProgress()) {
            const drawingBlocked = !this.canSendForDrawing() || this.hasCompletedDrawing();
            const drawingTip = this.hasCompletedDrawing()
                ? this.translate.instant('QUOTE.DRAWING.BLOCKER_ALREADY_DRAWN')
                : (this.sendForDrawingBlockers().length > 0
                    ? this.sendForDrawingBlockers().map(k => this.translate.instant(k)).join('\n')
                    : undefined);
            items.push({
                label: this.translate.instant('QUOTE.DRAWING.BTN_SEND_FOR_DRAWING'),
                icon: 'pi pi-pencil',
                disabled: drawingBlocked,
                tooltipOptions: drawingTip ? { tooltipLabel: drawingTip, tooltipPosition: 'left' } : undefined,
                command: () => this.onSendForDrawingClicked(),
            });
        }

        // Återta offert — revert from UnderReview / ReadyForReview
        if (versionStatus === QuoteStatus.UnderReview || versionStatus === QuoteStatus.ReadyForReview) {
            items.push({
                label: this.translate.instant('REVERT_QUOTE.TITLE'),
                icon: 'pi pi-replay',
                command: () => this.onRevertQuoteClicked(),
            });
        }

        // Markera som förlorad — terminating action. Always at the bottom, in danger tone.
        if (!terminal) {
            if (items.length > 0) items.push({ separator: true });
            items.push({
                label: this.translate.instant('MARK_AS_LOST.TITLE'),
                icon: 'pi pi-times-circle',
                styleClass: 'qa-menu-item--danger',
                command: () => this.onMarkAsLostClicked(),
            });
        }

        return items;
    });

    /** Hide the split-button when there's no meaningful primary OR alternative. */
    public readonly showSplitButton = computed(() =>
        this.showPrimaryBtn() && this.splitMenuItems().length > 0,
    );

    // =========================================================================
    // PUBLIC METHODS - Primary click handler
    // =========================================================================
    public onPrimaryClick(): void {
        if (this.isDraft()) {
            this.onSendQuoteClicked();
        } else if (this.isSent() || this.isUnderReview()) {
            this.onCreateOrderClicked();
        } else if (this.isReadyForReview()) {
            this.onReviewClicked();
        }
    }

    // =========================================================================
    // PUBLIC METHODS - Actions
    // =========================================================================
    /**
     * One dialog for the whole flow. The currency and credit-check steps used to be separate popups
     * opened in sequence; they are now conditional steps inside the stepper, which also fixes the
     * currency warning dead-ending instead of continuing to the send form.
     */
    public onSendQuoteClicked(): void {
        this.dialogService.open(SendQuoteStepperComponent, {
            header: this.translate.instant('SEND_QUOTE.TITLE'),
            closable: true,
            styleClass: 'dialog-container',
            style: { 'max-height': '95vh' },
            draggable: false,
            dismissableMask: true,
        });
    }

    /**
     * The review-or-order question no longer gets its own popup: both destinations are buttons on
     * the stepper's final step, so the choice is made with the credit result and comments visible.
     */
    public onCreateOrderClicked(): void {
        this.dialogService.open(CreateOrderStepperComponent, {
            header: this.translate.instant('QUOTE.DETAIL.BTN_CREATE_ORDER'),
            closable: true,
            styleClass: 'dialog-container',
            style: { 'max-height': '95vh' },
            draggable: false,
            dismissableMask: true,
        });
    }

    public onReviewClicked(): void {
        const selectedVersion = this.selectedVersion();

        if (selectedVersion?.exchangeInfo?.diffIsBig) {
            this.openCurrencyWarningDialog();
        } else {
            this.openReviewOrderDialog();
        }
    }

    public onDuplicateClicked(): void {
        const ref = this.dialogService.open(DuplicateQuoteWarningDialogComponent, {
            header: this.translate.instant('DUPLICATE_QUOTE.WARNING_TITLE'),
            closable: true,
            styleClass: 'dialog-container',
            draggable: false,
            dismissableMask: true,
        });

        ref.onClose.pipe(take(1)).subscribe((shouldDuplicate?: boolean) => {
            if (shouldDuplicate === undefined) return;
            if (shouldDuplicate) {
                this.openDuplicateQuoteDialog();
            } else {
                this.openCreateVersionDialog();
            }
        });
    }

    public onPreviewOrderClicked(): void {
        const quote = this.quote();
        const version = this.selectedVersion();
        const companyId = this.user()?.selectedCompany?.id;
        if (!quote || !version || !companyId) return;

        const stubRequest: CreateOrder = {
            commentIdsToInclude: [],
            translations: '{}',
            sendMail: false,
            subject: '',
            recipientEmail: '',
            ccEmails: [],
            message: '',
            comment: '',
        };

        this.quoteClient.previewOrder(quote.id, version.id, companyId, stubRequest).pipe(take(1)).subscribe({
            next: preview => {
                const blob = new Blob([JSON.stringify(preview, null, 2)], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                window.open(url, '_blank');
                // Release the URL after a short delay so the new tab has time to load it.
                setTimeout(() => URL.revokeObjectURL(url), 60_000);
            },
            error: err => {
                console.error('Preview order failed:', err);
            },
        });
    }

    public onMarkAsLostClicked(): void {
        this.dialogService.open(MarkAsLostReasonDialogComponent, {
            header: this.translate.instant('MARK_AS_LOST.TITLE'),
            closable: true,
            styleClass: 'dialog-container',
            draggable: false,
            dismissableMask: true,
        });
    }

    public onRevertQuoteClicked(): void {
        this.dialogService.open(RevertQuoteDialogComponent, {
            header: this.translate.instant('REVERT_QUOTE.TITLE'),
            closable: true,
            styleClass: 'dialog-container',
            style: { 'max-height': '95vh' },
            draggable: false,
            dismissableMask: true,
        });
    }

    // =========================================================================
    // PRIVATE METHODS - Dialog helpers
    // =========================================================================




    /**
     * Still standalone for the review path, which was not part of the stepper rework. Send-quote
     * folds this in as a step instead, so only this caller sees the old close-and-retry behaviour.
     */
    private openCurrencyWarningDialog(): void {
        this.dialogService.open(CurrencyWarningDialogComponent, {
            styleClass: 'dialog-container',
            style: { 'max-height': '95vh' },
            draggable: false,
            dismissableMask: true,
        });
    }

    private openReviewOrderDialog(): void {
        this.dialogService.open(ReviewOrderDialogComponent, {
            header: this.translate.instant('REVIEW_ORDER.TITLE'),
            closable: true,
            styleClass: 'dialog-container',
            style: { 'max-height': '95vh', width: '80vh' },
            draggable: false,
            dismissableMask: true,
        });
    }

    private openDuplicateQuoteDialog(): void {
        this.dialogService.open(DuplicateQuoteComponent, {
            header: this.translate.instant('DUPLICATE_QUOTE.TITLE'),
            closable: true,
            styleClass: 'dialog-container',
            style: { 'max-height': '95vh' },
            draggable: false,
            dismissableMask: true,
        });
    }

    public onGoToDrawing(): void {
        const companyId = this.user()?.selectedCompany?.id;
        const quoteId = this.quote()?.id;
        if (companyId && quoteId) {
            this.router.navigate([`/${companyId}/quote/${quoteId}/drawing`]);
        }
    }

    public onSendForDrawingClicked(): void {
        this.dialogService.open(SendForDrawingDialogComponent, {
            header: this.translate.instant("QUOTE.DRAWING.DIALOG.SEND_FOR_DRAWING_TITLE"),
            closable: true,
            styleClass: "dialog-container",
            draggable: false,
            dismissableMask: true,
        });
    }

    public onStartDrawingClicked(): void {
        this.dialogService.open(StartDrawingDialogComponent, {
            header: this.translate.instant("QUOTE.DRAWING.DIALOG.START_DRAWING_TITLE"),
            closable: true,
            styleClass: "dialog-container",
            draggable: false,
            dismissableMask: true,
        });
    }

    public onCompleteDrawingClicked(): void {
        this.dialogService.open(CompleteDrawingDialogComponent, {
            header: this.translate.instant("QUOTE.DRAWING.DIALOG.COMPLETE_DRAWING_TITLE"),
            closable: true,
            styleClass: "dialog-container",
            draggable: false,
            dismissableMask: true,
        });
    }

    public onRevokeDrawingClicked(): void {
        this.dialogService.open(RevokeDrawingDialogComponent, {
            header: this.translate.instant("QUOTE.DRAWING.DIALOG.REVOKE_TITLE"),
            closable: true,
            styleClass: "dialog-container",
            draggable: false,
            dismissableMask: true,
        });
    }

    private openCreateVersionDialog(): void {
        this.dialogService.open(CreateVersionDialogComponent, {
            header: this.translate.instant('CREATE_VERSION.TITLE'),
            closable: true,
            styleClass: 'dialog-container',
            style: { 'max-height': '95vh' },
            draggable: false,
            dismissableMask: true,
        });
    }
}
